import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { ButtonVariant, ButtonSize } from '../../types/common.types';
import { useResponsive } from '../../hooks/useResponsive';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  glowEffect?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  glowEffect = false,
  className = '',
  disabled,
  ...rest
}, ref) => {
  // Use responsive hook to adapt sizes
  const { isMobile } = useResponsive();
  
  // Base classes
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-300 focus:outline-none';
  
  // Size classes - Enhanced for better mobile touch targets
  const sizeClasses = {
    xs: isMobile ? 'text-xs px-2.5 py-1.5 min-h-[36px] min-w-[36px]' : 'text-xs px-2 py-1',
    sm: isMobile ? 'text-sm px-3 py-2 min-h-[40px] min-w-[40px]' : 'text-sm px-3 py-1.5',
    md: isMobile ? 'text-base px-4 py-2.5 min-h-[44px] min-w-[44px]' : 'text-base px-4 py-2',
    lg: isMobile ? 'text-lg px-4 py-2.5 min-h-[48px] min-w-[48px]' : 'text-lg px-5 py-2.5',
    xl: isMobile ? 'text-xl px-5 py-3 min-h-[52px] min-w-[52px]' : 'text-xl px-6 py-3',
  };
  
  // Variant classes
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white',
    secondary: 'bg-secondary-600 hover:bg-secondary-700 text-white',
    success: 'bg-green-600 hover:bg-green-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-amber-500 hover:bg-amber-600 text-white',
    info: 'bg-blue-500 hover:bg-blue-600 text-white',
    light: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
    dark: 'bg-gray-800 hover:bg-gray-900 text-white',
    link: 'bg-transparent text-primary-600 hover:text-primary-800 underline',
    outline: 'bg-transparent border border-primary-600 text-primary-600 hover:bg-primary-50',
    glass: 'glass-button text-white hover:bg-white/10',
  };
  
  // Disabled classes
  const disabledClasses = 'opacity-50 cursor-not-allowed';
  
  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';
  
  // Glow effect
  const glowClasses = glowEffect ? 'btn-glow' : '';
  
  // Add touch target class for mobile
  const touchTargetClass = isMobile ? 'improve-touch-target' : '';
  
  // Combine all classes
  const buttonClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${(disabled || isLoading) ? disabledClasses : ''}
    ${widthClasses}
    ${glowClasses}
    ${touchTargetClass}
    ${className}
  `.trim();
  
  // Calculate icon size based on button size
  const getIconSize = () => {
    switch(size) {
      case 'xs': return isMobile ? 14 : 12;
      case 'sm': return isMobile ? 16 : 14;
      case 'md': return isMobile ? 18 : 16;
      case 'lg': return isMobile ? 20 : 18;
      case 'xl': return isMobile ? 22 : 20;
      default: return isMobile ? 18 : 16;
    }
  };
  
  // Adjust spinner size based on button size
  const spinnerSize = getIconSize() * 1.2;
  
  return (
    <button
      ref={ref}
      className={buttonClasses}
      disabled={disabled || isLoading}
      {...rest}
    >
      {isLoading && (
        <svg 
          className="animate-spin -ml-1 mr-2" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
          width={spinnerSize}
          height={spinnerSize}
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          ></circle>
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      
      {!isLoading && leftIcon && (
        <span className={`mr-${isMobile ? '1.5' : '2'}`} style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          fontSize: getIconSize() 
        }}>
          {leftIcon}
        </span>
      )}
      
      <span className={isMobile ? 'mobile-text-adjust' : ''}>
        {children}
      </span>
      
      {!isLoading && rightIcon && (
        <span className={`ml-${isMobile ? '1.5' : '2'}`} style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          fontSize: getIconSize() 
        }}>
          {rightIcon}
        </span>
      )}
    </button>
  );
});

Button.displayName = 'Button';
export default Button;
